import { useEffect, useRef, useState } from "react";
import { History, Send, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAgentChat } from "@/components/AgentChatProvider";
import { AgentChatMessage } from "@/components/AgentChatMessage";
import { AgentWelcome, BOOKS_SUGGESTED_QUESTIONS } from "@/components/AgentWelcome";

function conversationLabel(c: { title: string | null; lastMessagePreview: string | null }) {
  if (c.title && c.title.trim().length > 0) return c.title;
  if (c.lastMessagePreview && c.lastMessagePreview.trim().length > 0) {
    return c.lastMessagePreview.slice(0, 40);
  }
  return "Untitled conversation";
}

export function AgentChatPanel() {
  const {
    messages,
    isStreaming,
    error,
    agent,
    agentName,
    sendMessage,
    clearMessages,
    toggleChat,
    recentConversations,
    loadConversation,
    refreshConversations,
    draft,
    takeDraft,
  } = useAgentChat();
  const [input, setInput] = useState("");
  // A prompt handed over by openChat(prompt) lands in the box for the person to send.
  useEffect(() => {
    if (draft === null) return;
    const d = takeDraft();
    if (d) setInput(d);
  }, [draft, takeDraft]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isNearBottom = useRef(true);

  // Track scroll position so we only auto-scroll when the user is at the bottom.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      isNearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    };
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isNearBottom.current && scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  function submit() {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    setInput("");
    sendMessage(trimmed);
  }

  function ask(question: string) {
    if (isStreaming) return;
    sendMessage(question);
  }

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    submit();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  }

  return (
    <div className="flex flex-col h-full bg-background border-r">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h2 className="flex items-center gap-2 font-semibold text-sm">
          <span aria-hidden="true" className="size-2 rounded-full bg-emerald-500 shadow-[0_0_0_3px] shadow-emerald-500/20" />
          {agentName} Agent
        </h2>
        <div className="flex gap-1">
          <DropdownMenu
            onOpenChange={(open) => {
              if (open) refreshConversations().catch(() => {});
            }}
          >
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Recent conversations">
                <History className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Most recent (3) conversations
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {recentConversations.length === 0 ? (
                <DropdownMenuItem disabled className="text-xs text-muted-foreground">
                  No conversations yet
                </DropdownMenuItem>
              ) : (
                recentConversations.map((c) => (
                  <DropdownMenuItem
                    key={c.id}
                    onSelect={() => {
                      loadConversation(c.id).catch(() => {});
                    }}
                    className="flex flex-col items-start gap-0.5"
                  >
                    <span className="text-sm truncate w-full">{conversationLabel(c)}</span>
                    {c.lastMessagePreview && c.title ? (
                      <span className="text-xs text-muted-foreground truncate w-full">
                        {c.lastMessagePreview}
                      </span>
                    ) : null}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={clearMessages} aria-label="Clear conversation">
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={toggleChat} aria-label="Close chat">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <AgentWelcome
            onAsk={ask}
            disabled={isStreaming}
            questions={agent === "books" ? BOOKS_SUGGESTED_QUESTIONS : undefined}
          />
        ) : (
          messages.map((m, i) => <AgentChatMessage key={m.id} message={m} isLatest={i === messages.length - 1} />)
        )}
      </div>

      <div className="border-t p-3">
        <form onSubmit={handleSubmit} className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="min-h-10 max-h-32 resize-none"
            disabled={isStreaming}
          />
          <Button type="submit" size="icon" className="shrink-0" disabled={!input.trim() || isStreaming}>
            {isStreaming ? <Spinner className="h-4 w-4" /> : <Send className="h-4 w-4" />}
          </Button>
        </form>
        {error && <p className="text-xs text-destructive mt-2">{error}</p>}
      </div>
    </div>
  );
}
